import { IconButton } from 'design-system';
import { Copy } from 'lucide-react';
import { useMemo } from 'react';

import LoadingIndicator from '@/features/ai-chat/components/AskGXDrawer/LoadingIndicator/LoadingIndicator';
import LoadingText from '@/features/ai-chat/components/AskGXDrawer/LoadingText/LoadingText';
import RateAnswer from '@/features/ai-chat/components/AskGXDrawer/Messages/RateAnswer/RateAnswer';
import type { CatalogRow } from '@/features/ai-chat/types/chat.types';
import copyToClipboard from '@/shared/lib/copyToClipboard';

import type { UIMessage } from '@tanstack/ai-client';

function rowsFromToolOutput(output: unknown): CatalogRow[] {
  if (!output || typeof output !== 'object' || !('rows' in output)) return [];
  const rows = output.rows;
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the render_table adapter validates/catalogues this backend-owned row shape.
  return Array.isArray(rows) ? (rows as CatalogRow[]) : [];
}

const MessageFooter = ({
  msg,
  isLastMessage,
  isSending,
  version,
  prompt,
  conversationId,
  rating,
  onRate
}: {
  msg: UIMessage;
  isLastMessage: boolean;
  isSending: boolean;
  version: number;
  prompt: string;
  conversationId: string | null;
  rating: -1 | 0 | 1;
  onRate: (id: string, score: -1 | 1) => void;
}) => {
  const response = useMemo<Array<string | CatalogRow[]>>(() => {
    return msg.parts.reduce<Array<string | CatalogRow[]>>((acc, part) => {
      if (part.type === 'text') acc.push(part.content);
      if (part.type === 'tool-call' && part.name === 'render_table' && part.state === 'complete') {
        acc.push(rowsFromToolOutput(part.output));
      }
      return acc;
    }, []);
  }, [msg.parts]);

  const textResponse = useMemo(
    () =>
      msg.parts
        .filter(part => part.type === 'text')
        .map(part => part.content)
        .join(''),
    [msg.parts]
  );

  return (
    <div
      className={`mt-3 items-center justify-between ${isLastMessage && !isSending ? 'flex' : ''}`}
    >
      {isLastMessage && isSending && <LoadingText firstChunkArrived={msg.parts.length > 0} />}
      {isLastMessage && isSending && <LoadingIndicator animate />}
      {((isLastMessage && !isSending) || !isLastMessage) && (
        <div className='flex w-full items-center justify-between border-t border-gx-border/55 pt-2 text-gx-text-disabled'>
          <span className='text-[11px]'>Verify important details</span>
          <div className='flex items-center gap-1'>
            <IconButton
              label='Copy response'
              size='icon-sm'
              variant='ghost'
              onClick={() => {
                void copyToClipboard(textResponse, 'Response copied to clipboard!');
              }}
            >
              <Copy className='size-5' />
            </IconButton>
            <RateAnswer
              conversationId={conversationId || ''}
              prompt={prompt}
              rating={rating}
              response={response}
              version={version}
              onRate={score => {
                onRate(msg.id, score);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageFooter;
